import { Component, Element, Event, EventEmitter, h, Method, Prop, Watch } from '@stencil/core';
import Bind from 'bind-decorator';
import { LinkTarget } from '../../../types';
import { whenActuallyActivated } from '../../../utils/accessibility';
import { hasSlotted } from '../../../utils/slotted';
import { NavigationMaybeKeyboardEventDetail } from '../zds-navigation';

@Component({
	shadow: true,
	styleUrl: 'zds-navigation-body-link.scss',
	tag: 'zds-navigation-body-link',
})
export class ZdsNavigationBodyLink {
	@Element() hostElement: HTMLDivElement;

	@Event({ bubbles: true }) zdsNavigationActivatePanel: EventEmitter<NavigationMaybeKeyboardEventDetail>;

	@Prop({ reflect: true }) active: boolean;
	@Prop() link: string;
	@Prop() linkTarget: LinkTarget = '_self';

	@Watch('active')
	onActiveChange() {
		this.hostElement.setAttribute('aria-expanded', this.active.toString());
	}

	@Method()
	async activate(isKeyboardActivated: boolean = false) {
		if (this.link) return;

		if (!this.isSlottedInPanel()) {
			return;
		}

		const parentElement = this.hostElement.parentElement as HTMLZdsNavigationPanelElement;
		const identifier = parentElement.identifier;

		this.zdsNavigationActivatePanel.emit({
			identifier,
			isKeyboardActivated,
		});
	}

	isSlottedInPanel(): boolean {
		return (
			this.hostElement.parentElement.localName === 'zds-navigation-panel' &&
			this.hostElement.getAttribute('slot') === 'body-link'
		);
	}

	@Bind
	async onPotentialActivate(event: MouseEvent | KeyboardEvent) {
		whenActuallyActivated(event, () => void this.activate(event.type === 'keydown'), {
			preventDefault: !this.link,
		});
	}

	render() {
		let ComponentTag = 'div';
		let attributes = {};

		if (this.link) {
			ComponentTag = 'a';
			attributes = {
				...attributes,
				href: this.link,
				target: this.linkTarget,
				role: 'link',
				'aria-expanded': this.active,
			};
		}

		return (
			<ComponentTag
				class="zds-navigation-body-link"
				onClick={this.onPotentialActivate}
				onKeyDown={this.onPotentialActivate}
				tabindex="0"
				{...attributes}
			>
				{this.renderIcon()}

				<span class="zds-navigation-body-link__label">
					<slot />
				</span>
				{this.isSlottedInPanel() && (
					<zds-icon
						class="zds-navigation-body-link__icon zds-navigation-body-link__icon-forward"
						name="chevron-right"
						library="fa-solid"
						size={4}
					></zds-icon>
				)}
			</ComponentTag>
		);
	}

	renderIcon() {
		if (!hasSlotted(this.hostElement, 'icon')) {
			return null;
		}

		return (
			<span class="zds-navigation-body-link__icon">
				<slot name="icon"></slot>
			</span>
		);
	}
}
